// frontend/src/components/forms/NumericTextField.tsx
//
// BUG-2026-0029 follow-up — a controlled decimal input that doesn't suffer
// from `<input type="number">`'s sanitization quirks.
//
// Problem with `<input type="number">` + controlled React state:
//   Typing "14.06" goes 14 → "14." → "14.0" → "14.06". HTML5 sanitizes
//   incomplete floats like "14." to "" via `node.value`. Even when the raw
//   string IS valid, the controlled value Round-trips through `Number()`:
//     Number("14.0") === 14, then String(14) === "14"  ← trailing ".0" lost.
//   React then overwrites the DOM `.value` and the next keystroke ("6")
//   appends to "14" → final value 146 instead of 14.06.
//
// The fix in this wrapper:
//   - `type="text"` + `inputMode="decimal"` — browser does NO sanitization,
//     `e.target.value` always returns the user's literal typing, and mobile
//     keyboards still show a decimal keypad.
//   - Keep the user's draft text in local React state. Only commit a parsed
//     number to the parent when the draft is a complete, finite number;
//     incomplete drafts ("-", ".", "14.") commit `null`.
//   - When the parent's value changes externally (server load, derived
//     computations) AND it doesn't round-trip to our current draft, the
//     draft is replaced. Otherwise the draft is preserved, so the user's
//     "14.0" stays visible while the parent stores 14.
//
// Accepts every prop a MUI `<TextField>` does (label, placeholder, sx,
// size, disabled, InputProps, etc.) — only `value`, `onChange`, and `type`
// are taken over.

import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { TextField, type TextFieldProps } from '@mui/material';

export type NumericTextFieldProps = Omit<
  TextFieldProps,
  'value' | 'onChange' | 'type'
> & {
  value: number | null | undefined;
  onChange: (next: number | null) => void;
};

const DECIMAL_RE = /^-?\d*\.?\d*$/;

function isIncompleteDraft(raw: string): boolean {
  return raw === '' || raw === '-' || raw === '.' || raw === '-.';
}

function valueToText(v: number | null | undefined): string {
  return v === null || v === undefined ? '' : String(v);
}

export function NumericTextField({
  value,
  onChange,
  inputProps,
  ...rest
}: NumericTextFieldProps) {
  const [draft, setDraft] = useState<string>(() => valueToText(value));
  // Track the last value we observed (either via parent prop or our own
  // commit), so we can tell when the parent supplied a NEW value externally.
  const lastValueRef = useRef<number | null | undefined>(value);

  useEffect(() => {
    if (lastValueRef.current === value) return;
    lastValueRef.current = value;
    // Preserve the draft if it still round-trips to the new value (this is
    // what keeps "14.0" visible while the parent stores 14).
    if (value === null || value === undefined) {
      if (isIncompleteDraft(draft)) return;
    } else if (draft !== '' && Number(draft) === value) {
      return;
    }
    setDraft(valueToText(value));
  }, [value, draft]);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const raw = e.target.value;
    // Filter out anything that isn't part of a decimal number. Reject the
    // keystroke (do NOT update state) so the cursor stays where it was.
    if (raw !== '' && !DECIMAL_RE.test(raw)) return;
    setDraft(raw);

    if (isIncompleteDraft(raw)) {
      lastValueRef.current = null;
      onChange(null);
      return;
    }
    const n = Number(raw);
    if (!Number.isFinite(n)) {
      lastValueRef.current = null;
      onChange(null);
      return;
    }
    lastValueRef.current = n;
    onChange(n);
  };

  return (
    <TextField
      {...rest}
      type="text"
      value={draft}
      onChange={handleChange}
      inputProps={{ inputMode: 'decimal', ...(inputProps ?? {}) }}
    />
  );
}
